/**
 * This file contains utility functions for slide scanners.
 * It is used to:
 *   - Display the slide scanners in the order detail page.
 *   - Validate the scanner and file compatibility.
 *   - Get the supported file extensions for a given workflow or scanner type.
 */
import { getSamdWorkflowDefinition } from '@samd/workflows';
import {
  SCANNER_SUPPORTED_EXTENSIONS,
  SCANNER_TYPE_OPTIONS,
} from 'src/distribution_platforms/features/utils/slide_scanner_constants';
import { SlideImageScannerType, type SpecimenType } from 'src/types';
import type { WorkflowType } from 'src/types/workflows';
import { getFileExtension } from 'src/utils';

/**
 * Get scanner options for a SaMD workflow (defined by the workflow's supported scanners).
 */
export const getScannerOptionsForWorkflow = (
  workflowType: WorkflowType,
  specimenType: SpecimenType,
) => {
  const supportedScanners =
    getSamdWorkflowDefinition(workflowType).getSupportedSlideScanners(
      specimenType,
    );
  return Array.from(supportedScanners, (scannerType) => {
    const option = SCANNER_TYPE_OPTIONS.find(
      ({ value }) => value === scannerType,
    );
    if (!option) {
      throw new Error(`Missing scanner option for type: ${scannerType}`);
    }
    return option;
  });
};

/**
 * Get all supported file extensions for a SaMD workflow.
 * Combines extensions from all scanners supported by that workflow.
 */
export const getSupportedFileExtensions = (
  workflowType: WorkflowType,
  specimenType: SpecimenType,
): string[] => {
  const supportedScanners =
    getSamdWorkflowDefinition(workflowType).getSupportedSlideScanners(
      specimenType,
    );
  const extensionsSet = new Set<string>();

  // Collect all extensions from all supported scanners
  for (const scanner of supportedScanners) {
    const extensions = SCANNER_SUPPORTED_EXTENSIONS[scanner];
    for (const ext of extensions) {
      extensionsSet.add(ext);
    }
  }

  return Array.from(extensionsSet);
};

/**
 * Check if a file extension is supported by a scanner type
 */
export const isFileExtensionSupported = (
  filename: string,
  scannerType: SlideImageScannerType,
): boolean => {
  const extension = getFileExtension(filename);
  const supportedExtensions = SCANNER_SUPPORTED_EXTENSIONS[scannerType];
  return supportedExtensions.has(extension);
};

/**
 * Validate scanner and file compatibility for a SaMD workflow.
 */
export const validateScannerFileCompatibility = (
  filename: string,
  scannerType: SlideImageScannerType | null,
  workflowType: WorkflowType,
  specimenType: SpecimenType,
): string | null => {
  if (!scannerType) {
    return 'Please select a scanner type';
  }

  // Check if scanner is supported in this region
  const supportedScanners =
    getSamdWorkflowDefinition(workflowType).getSupportedSlideScanners(
      specimenType,
    );
  if (!supportedScanners.has(scannerType)) {
    return `${SCANNER_TYPE_OPTIONS.find((opt) => opt.value === scannerType)?.name} is not supported for this workflow`;
  }

  // Check if file extension is compatible with scanner
  if (!isFileExtensionSupported(filename, scannerType)) {
    const extension = getFileExtension(filename);
    const supportedExtensions = Array.from(
      SCANNER_SUPPORTED_EXTENSIONS[scannerType],
    ).join(', ');
    return `File extension "${extension}" is not supported by the selected scanner. Supported extensions: ${supportedExtensions}`;
  }

  return null;
};

/**
 * Get human-readable list of supported file types for a SaMD workflow.
 */
export const getSupportedFileTypesText = (
  workflowType: WorkflowType,
  specimenType: SpecimenType,
): string => {
  const extensions = getSupportedFileExtensions(workflowType, specimenType);
  return extensions.map((ext) => ext.toUpperCase()).join(', ');
};
